import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareBar from "@/components/feature/ShareBar";
import AnimeCard from "@/components/feature/AnimeCard";
import usePageMeta from "@/hooks/usePageMeta";
import useFavorites from "@/hooks/useFavorites";
import { animes } from "@/mocks/animes";

export default function AnimeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const anime = animes.find((a) => a.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta(
    anime ? `${anime.title} - 火狼动漫网` : "作品不存在 - 火狼动漫网",
    anime?.synopsis,
  );

  if (!anime) {
    return (
      <div className="w-full px-4 md:px-8 py-20">
        <div className="max-w-2xl mx-auto flex flex-col items-center justify-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
            <i className="ri-movie-2-line text-3xl"></i>
          </span>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">作品不存在或已下架</h1>
          <p className="text-sm text-foreground-500 mb-6">你访问的作品可能已被移除，或链接有误。</p>
          <Link
            to="/anime"
            className="px-6 h-11 inline-flex items-center rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回作品库
          </Link>
        </div>
      </div>
    );
  }

  const fav = isFavorite(anime.id);

  const related = animes
    .filter((a) => a.id !== anime.id && a.genres.some((g) => anime.genres.includes(g)))
    .concat(animes.filter((a) => a.id !== anime.id))
    .slice(0, 4);

  const infoItems = [
    { label: "类型", value: anime.genres.join(" / ") },
    { label: "地区", value: anime.region },
    { label: "年份", value: `${anime.year} 年` },
    { label: "状态", value: anime.status },
    { label: "集数", value: `${anime.episodes} 集` },
    { label: "评分", value: anime.rating.toFixed(1) },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[{ label: "动漫作品", to: "/anime" }, { label: anime.title }]} />

        <div className="mt-6 rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
          <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-8">
            <div className="w-full max-w-[240px] mx-auto md:mx-0">
              <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                <img
                  src={anime.cover}
                  alt={`${anime.title} 作品封面`}
                  title={`${anime.title} - 火狼动漫网动漫作品`}
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute top-3 right-3 px-2.5 py-1 rounded-md bg-accent-500 text-primary-950 text-sm font-bold">
                  {anime.rating.toFixed(1)}
                </span>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                    {anime.title}
                  </h1>
                  <div className="flex items-center flex-wrap gap-2">
                    {anime.genres.map((g) => (
                      <span
                        key={g}
                        className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-800 text-xs font-medium"
                      >
                        {g}
                      </span>
                    ))}
                    <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-900 text-xs font-medium">
                      {anime.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleFavorite(anime.id)}
                    className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      fav
                        ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                        : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
                    </span>
                    {fav ? "已收藏" : "收藏"}
                  </button>
                </div>
              </div>

              <div className="mt-5 rounded-md bg-background-50 border border-background-200 overflow-hidden">
                <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-y divide-background-200">
                  {infoItems.map((item) => (
                    <div key={item.label} className="p-3.5">
                      <div className="text-xs text-foreground-400 mb-1">{item.label}</div>
                      <div className="text-sm font-medium text-foreground-800">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex items-center flex-wrap gap-2">
                {anime.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs"
                  >
                    # {t}
                  </span>
                ))}
              </div>

              <div className="mt-5">
                <ShareBar title={`${anime.title} - 火狼动漫网`} />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <section className="lg:col-span-2">
            <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              剧情简介
            </h2>
            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <p className="text-base text-foreground-700 leading-loose">{anime.synopsis}</p>
              <div className="mt-5 pt-5 border-t border-background-200 flex items-center gap-2 text-xs text-foreground-400">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-information-line"></i>
                </span>
                本站不提供任何播放与下载服务，以上内容仅用于作品信息展示。
              </div>
            </div>
          </section>

          <aside>
            <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {related.map((item) => (
                <AnimeCard key={item.id} anime={item} />
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}